import HttpRequest from '@/../bower_components/teknisavuecomponents/assets/utils/HttpRequest';
import store from '@/store/store';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import ExpectedError from '@/../bower_components/teknisavuecomponents/assets/utils/ExpectedError';
import Router from '@/Router';
import Messages from '@/assets/ts/utils/Messages';
import Util from '@/../bower_components/teknisavuecomponents/assets/utils/Util';
import axios from 'axios';
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx";

export enum ExportType {
  PDF = 'pdf',
  XLS = 'xls'
}


/**
 * nos dois parametros do origin eu passo o titulo q aparece no topo do relatorio
 */
type ReportParams = {
  origin: {
      containerLabel: string;
      widgetLabel: string;
  };
  requestType: 'Row';
  row: {
      dataSet: string;
      filter: string;
      metaData: string;
  };
};

/**
 * Definição das colunas. É um objeto onde as chaves são os nomes das colunas
 * e o valor é um objeto com as propriedades da coluna
 * 
 * `description`: é a label da coluna \
 * `sequence`: indice da coluna (ordenação) \
 * `size`: tamanho da coluna (em %)
 */
type MetadataColumn = Record<string, {
  align: 'left' | 'center' | 'right';
  description: string;
  sequence: number;
  size: string;
  expression?: string;
  xlsType?: string;
}>;

/**
 * Filtros. label é a coluna do filtro e value é o filtro aplicado,
 * ex { label: 'CDFILIAL', value: 'igual a 00001' }
 */
type MetadataFilter = {
  label: string;
  value: string;
}[];

/**
 * `logos`: string da imagem codificada em base64 (ou string vazia)
 * `name` e `reportName`: nome do relatorio \
 * `title`: titulo q aparece no topo \
 * `widgetLabel` = title \
 * `words`: não lembro o que é esse words. Aqui só passamos a tradução dessas palavras \
 * pt-br: { Page: 'Página', Report: 'Relatório', Version: 'Versão' } \
 * en-us: { Page: 'Page', Report: 'Report', Version: 'Version' } \
 * `staticData`: sempre passamos um objeto vazio {} \
 * `groups`: sempre passamos um array vazio [] \
 */
type MetadataObj = {
  clientLogo: string;
  enterpriseLogo: string;
  productLogo: string;
  columns: MetadataColumn;
  filter: MetadataFilter;
  lang: 'pt_br' | 'en_us' | 'es_es';
  name: string;
  reportName: string;
  title: string;
  widgetLabel: string;
  orientation: 'PORTRAIT' | 'LANDSCAPE';
  version: '1.0';
  words: { Page: string, Report: string, Version: string };
  staticData: {};
  groups: Array<any>;
  // os proximos é só se for gerar xls
  reportXLS?: boolean;
  formatXLS?: 'F1' | 'F2' | 'F3';
  type?: string;
  xlsMergedCell?: any[];
}

class ExportRelatController {

  // #region [ PDF ]
  public static generatePDF(
    fileName: string,
    title: string,
    rows: Array<any>, 
    columns: Array<string>, 
    columnMapping: Record<string, string>
  ) {

    const doc = new jsPDF({
      orientation: "landscape",
    });
  
    const titleWidth = doc.getTextWidth(title);
    const xPosition = (doc.internal.pageSize.width - titleWidth) / 2;
    doc.text(title, xPosition, 10);
  
    // Define o conteúdo da tabela dinamica mente
    const tableBody = rows.map(item =>
      columns.map(column => item[columnMapping[column]] || "")
    );
  
    // Adiciona a tabela ao PDF
    autoTable(doc, {
      head: [columns],
      body: tableBody,
      theme: "plain",
    });
  
    // Salva o arquivo
    doc.save(fileName + ".pdf");
  };
  // #endregion

  // #region [ XLSX ]
  public static generateXLSX(
    fileName: string,
    data: Array<any>,
    columns: Array<string>,
    columnMapping: Record<string, string>,
    columnWidths?: Record<string, number>
  ) {
    // 1. Transformar os dados com base no mapeamento das colunas
    const transformedData = this.transformData(data, columns, columnMapping);
  
    // 2. Criar a planilha com os dados transformados
    const worksheet = XLSX.utils.json_to_sheet(transformedData);
  
    // 3. Definir a largura das colunas
    if (columnWidths) {
      // Se a largura das colunas for fornecida manualmente, usamos ela
      this.setManualColumnWidths(worksheet, columns, columnWidths);
    } else {
      // Caso contrário, calculamos a largura automaticamente
      this.setAutoColumnWidths(worksheet, columns, transformedData);
    }
  
    // 4. Criar o Workbook e salvar o arquivo
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dados");
  
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }
  
  // Função para transformar os dados com base no mapeamento
  private static transformData(
    data: Array<any>, 
    columns: Array<string>, 
    columnMapping: Record<string, string>
  ): Array<any> {
    return data.map(item => {
      const row: Record<string, any> = {};
      columns.forEach(column => {
        // Mapeia o título da coluna para o campo correspondente no item de dados
        row[column] = item[columnMapping[column]] || ""; 
      });
      return row;
    });
  }
  
  // Função para definir as larguras das colunas manualmente
  private static setManualColumnWidths(
    worksheet: any, 
    columns: Array<string>, 
    columnWidths: Record<string, number>
  ) {
    worksheet['!cols'] = columns.map(column => ({
      wpx: columnWidths[column] || 100 // Se não definir a largura, usa 100 por padrão
    }));
  }
  
  // Função para definir as larguras das colunas automaticamente com base no conteúdo
  private static setAutoColumnWidths(
    worksheet: any, 
    columns: Array<string>,
    transformedData: Array<any>
  ) {
    const maxColumnWidths: Record<string, number> = {};
  
    // Calcular a largura máxima de cada coluna
    columns.forEach(column => {
      const columnValues = transformedData.map(row => row[column]);
      const maxLengthRow = Math.max(...columnValues.map(value => (value ? value.toString().length : 0)));
      const maxLengthTitle = column.length;
  
      // Calcula o comprimento máximo, limitado ao valor de 50
      const maxLength = Math.min(Math.max(maxLengthRow, maxLengthTitle), 50);
      maxColumnWidths[column] = maxLength + 2; // +2 para espaço extra
    });
    
  
    // Define as larguras das colunas no formato esperado pelo XLSX
    worksheet['!cols'] = columns.map(column => ({
      wpx: ((maxColumnWidths[column] * 10) / 2) + 30 // Multiplica por 10 para ajustar a largura
    }));
  }
  // #endregion




  // #region [ Zeedhi ]
  public static async exportData(
    data: any,
    type: string,
    name: string,
    columns: string[],
    filter: any = null
  ): Promise<boolean>  {
    try {
      let requestUrl = '';
      if(type == 'pdf') {
        requestUrl = 'https://zhreport.teknisa.com/generateReport';
      } else if(type == 'xls') {
        requestUrl = 'https://zhreport.teknisa.com/generateXLS';
      } else {
        throw new Error("Tipo de exportação não encontrado pra: " + type);
      }

      const baseUrl = 'https://zhreport.teknisa.com/reports/';

      const params = await this.prepareVariables(name, data, columns, type, filter);

      const response = await axios.post(requestUrl, params);

      window.open(baseUrl + response.data.messages[0].message);

      return true;
    } catch (err) {
      const e = err as any;
      const message = e.message;

      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      throw err;
    }
  }

  private static async prepareVariables(relatName, row, columns, type, filter = null) {
    let metaDataFilter : MetadataFilter = [];

    if(!Util.isEmptyOrBlank(filter)) {
      filter.forEach(filtro => {
        metaDataFilter.push({
          label: filtro.name,
          value: filtro.value
        })
      });
    }

    const metaDataColumns : MetadataColumn = {};


    columns.forEach((col, index) => {
      metaDataColumns[col] = {
        align: 'left',
        description: col,
        sequence: index,
        size: '25'
      }
      
    });

    const metadata : MetadataObj = {
      clientLogo: '',
      enterpriseLogo: '',
      productLogo: '',
      columns: metaDataColumns,
      filter: [],
      lang: 'pt_br',
      name: relatName,
      reportName: relatName,
      title: relatName,
      widgetLabel: relatName,
      orientation: 'PORTRAIT',
      version: '1.0',
      words: {
        Page: 'Página', 
        Report: 'Relatório', 
        Version: 'Versão' 
      },
      staticData: {},
      groups: []
    }

    if(type == 'xls') {
      metadata.reportXLS = true;
      metadata.formatXLS = 'F3';
    }

    const params: ReportParams = {
      origin: {
        containerLabel: relatName,
        widgetLabel: relatName
      },
      requestType: 'Row',
      row: {
        dataSet: JSON.stringify(row),
        filter: JSON.stringify(metaDataFilter),
        metaData: JSON.stringify(metadata)
      }
    }
  
    return params;
  
  }
}


export {ExportRelatController};